import { useEffect, useState } from "react";

/**
 * "light": a logo drawn mostly in white or near-white on transparency, which
 * disappears on a light surface and needs a dark one (a "reversed" logo).
 * "dark": everything else that is transparent. "opaque": the logo carries
 * its own background, so any surface works.
 */
export type LogoTone = "light" | "dark" | "opaque";

const SAMPLE_SIZE = 64;

/** Classifies RGBA pixels. Exported for tests; null when nothing is visible. */
export function logoToneFromPixels(data: Uint8ClampedArray): LogoTone | null {
  let visible = 0;
  let transparent = 0;
  let nearWhite = 0;
  let nearBlack = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 128) {
      transparent++;
      continue;
    }
    visible++;
    const luminance = (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
    if (luminance > 0.85) nearWhite++;
    else if (luminance < 0.2) nearBlack++;
  }
  if (!visible) return null;
  if (transparent / (visible + transparent) < 0.05) return "opaque";
  // Choose the surface that hides less of the logo.
  return nearWhite > nearBlack && nearWhite / visible >= 0.25 ? "light" : "dark";
}

const tones = new Map<string, Promise<LogoTone | null>>();

function measure(src: string): Promise<LogoTone | null> {
  let pending = tones.get(src);
  if (!pending) {
    pending = new Promise((resolve) => {
      const image = new Image();
      // Storage serves CORS for the app; without it the canvas can't be read.
      image.crossOrigin = "anonymous";
      image.onload = () => {
        try {
          const scale = Math.min(1, SAMPLE_SIZE / Math.max(image.naturalWidth, image.naturalHeight, 1));
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
          canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
          const context = canvas.getContext("2d");
          if (!context) return resolve(null);
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          resolve(logoToneFromPixels(context.getImageData(0, 0, canvas.width, canvas.height).data));
        } catch {
          resolve(null);
        }
      };
      image.onerror = () => resolve(null);
      image.src = src;
    });
    tones.set(src, pending);
  }
  return pending;
}

/** The logo's tone once measured; null while loading or if it can't be read. */
export function useLogoTone(src: string | null | undefined): LogoTone | null {
  const [result, setResult] = useState<{ src: string; tone: LogoTone | null } | null>(null);
  useEffect(() => {
    if (!src) return;
    let current = true;
    void measure(src).then((tone) => {
      if (current) setResult({ src, tone });
    });
    return () => {
      current = false;
    };
  }, [src]);
  return result && result.src === src ? result.tone : null;
}

/** Surface for a logo preview: dark for a light logo, otherwise white. */
export function logoSurfaceClass(tone: LogoTone | null): string {
  return tone === "light" ? "bg-neutral-800" : "bg-white";
}
